import { handleJSON, handleJSONForForm } from '@/utils/fetch-utils';
import { headers } from './index';

const { API_URL } = process.env;

let abortController = new AbortController();

// noinspection JSUnusedGlobalSymbols
export default {
  endpoints: {
    manageLearnersEngagementReport: id => `${API_URL}/manage-learners-engagement-report/?user_id=${id}`,
    manageLearnersReport: `${API_URL}/manage-learners-report/`,
    manageLearnersSummary: `${API_URL}/manage-learners-summary/`,
    profile: id => {
      return id ? `${API_URL}/users/${id}` : `${API_URL}/users`;
    },
    resetPassword: `${API_URL}/reset-password/`,
    batch: `${API_URL}/users/batch/`,
    batchUpdate: `${API_URL}/users/batch/update/`,
    batchScheduledLearners: `${API_URL}/users/batch/schedule-activation/`,
    privateClassesUpdateBatch: `${API_URL}/users/batch/update-private-class-credits/`,
    privateClassHoursAllocateBatch: `${API_URL}/users/batch/allocate-private-class-hours/`,
    vpaHistory: `${API_URL}/user_vpas/`,
    resendLearnerEmail: `${API_URL}/resend-activation-email/`,
    makeVpaAvailableNow: `${API_URL}/make-vpa-available/`,
    permissions: userId => `${API_URL}/users/${userId}/permissions`,
  },

  batchUpdateLearners({ file, organizationId }) {
    const endpoint = this.endpoints.batchUpdate;
    const formData = new FormData();
    formData.append('organization_id', organizationId);
    formData.append('filename', file);

    const customHeaders = {
      authorization: headers().authorization,
    };
    const options = {
      method: 'POST',
      headers: customHeaders,
      body: formData,
    };

    return fetch(endpoint, options).then(handleJSON);
  },

  privateClassesCreditsUpdateBatch({ file, organizationId }) {
    const endpoint = this.endpoints.privateClassesUpdateBatch;
    const formData = new FormData();
    formData.append('filename', file);
    formData.append('organization_id', organizationId);

    const customHeaders = {
      authorization: headers().authorization,
    };
    const options = {
      method: 'POST',
      headers: customHeaders,
      body: formData,
    };

    return fetch(endpoint, options).then(handleJSON);
  },

  privateClassHoursAllocateBatch({ file, organizationId }) {
    const endpoint = this.endpoints.privateClassHoursAllocateBatch;
    const formData = new FormData();
    formData.append('filename', file);
    formData.append('organization_id', organizationId);

    const customHeaders = {
      authorization: headers().authorization,
    };
    const options = {
      method: 'POST',
      headers: customHeaders,
      body: formData,
    };

    return fetch(endpoint, options).then(handleJSON);
  },

  fetchLearnerProfile({ learnerId }) {
    const options = {
      method: 'GET',
      headers: headers(),
    };
    const endpoint = this.endpoints.profile(learnerId);

    return fetch(endpoint, options).then(handleJSON);
  },

  fetchLearnerReport({ learnerId }) {
    const options = {
      method: 'GET',
      headers: headers(),
    };
    const endpoint = `${this.endpoints.manageLearnersReport}${learnerId}/`;

    return fetch(endpoint, options).then(handleJSON);
  },

  fetchEngagementReport({ learnerId }) {
    const options = {
      method: 'GET',
      headers: headers(),
    };
    const endpoint = this.endpoints.manageLearnersEngagementReport(learnerId);

    return fetch(endpoint, options).then(handleJSON);
  },

  fetchLearners({
    organizationId,
    pageSize,
    currentPage,
    ordering,
    search,
    dateRange,
    include = [],
    format = 'json',
    callback = handleJSON,
    filters,
  }) {
    const offset = currentPage * pageSize;
    const formattedSearch = search == null ? '' : search;

    const fields = include.join(',');
    const filterParams = Object.keys(filters)
      .map(key => ({
        key: filters[key].key,
        value: filters[key].value,
      }))
      .filter(param => param.value);
    const { start, end } = dateRange;

    let params = [
      {
        key: 'organization_id',
        value: organizationId,
      },
      {
        key: 'limit',
        value: pageSize,
      },
      {
        key: 'offset',
        value: offset,
      },
      {
        key: 'ordering',
        value: ordering,
      },
      {
        key: 'search',
        value: formattedSearch,
      },
      {
        key: 'format',
        value: format,
      },
      {
        key: 'include',
        value: fields,
      },
      {
        key: 'start_date',
        value: start,
      },
      {
        key: 'end_date',
        value: end,
      },
    ];

    params = [...params, ...filterParams];

    params = params.map(param => `${param.key}=${param.value}`);
    const paramsQuery = params.join('&');
    const endpoint = `${this.endpoints.manageLearnersSummary}?${paramsQuery}`;

    const customHeaders = headers();
    if (format === 'csv') {
      customHeaders.Accept = 'text/csv';
    } else if (format === 'xlsx') {
      customHeaders.Accept = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }

    abortController.abort();
    abortController = new AbortController();

    const options = {
      method: 'GET',
      headers: customHeaders,
      signal: abortController.signal,
    };

    return fetch(endpoint, options).then(callback);
  },

  fetchUserPermissions(userId) {
    const options = {
      method: 'GET',
      headers: headers(),
    };
    const endpoint = this.endpoints.permissions(userId);

    return fetch(endpoint, options).then(handleJSON);
  },

  updateUserPermissions(userId, permissions) {
    const options = {
      method: 'PUT',
      headers: headers(),
      body: JSON.stringify(permissions),
    };
    const endpoint = this.endpoints.permissions(userId);

    return fetch(endpoint, options).then(handleJSON);
  },

  resendActivationEmail(email) {
    const options = {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ email }),
    };
    const endpoint = this.endpoints.resendLearnerEmail;

    return fetch(endpoint, options).then(handleJSON);
  },

  makeVpaAvailableNow(email) {
    const options = {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ email }),
    };
    const endpoint = this.endpoints.makeVpaAvailableNow;

    return fetch(endpoint, options).then(handleJSON);
  },

  saveLearner(learner) {
    const learnerData = {
      first_name: learner.firstName,
      last_name: learner.lastName,
      email: learner.email,
      native_language: learner.nativeLanguage,
      phone_number: learner.phoneNumber,
      country_code: learner.countryCode,
      timezone: learner.timeZone && learner.timeZone.replace(/ /g, '_'),
      group_id: learner.featureGroup,
      external_user_id: learner.externalUserId,
      level_id: learner.levelId,
      membership_end_date: learner.membershipEndDate,
    };
    const options = {
      method: learner.id ? 'PUT' : 'POST',
      headers: headers(),
      body: JSON.stringify(learnerData),
    };
    const endpoint = this.endpoints.profile(learner.id);

    return fetch(endpoint, options).then(handleJSONForForm);
  },

  setFeatureGroup({ learnerId, featureGroup }) {
    const learnerData = {
      group_id: featureGroup.id,
    };
    const options = {
      method: 'PATCH',
      headers: headers(),
      body: JSON.stringify(learnerData),
    };
    const endpoint = this.endpoints.profile(learnerId);

    return fetch(endpoint, options).then(handleJSON);
  },

  resetPassword(email) {
    const options = {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify({ email }),
    };

    return fetch(this.endpoints.resetPassword, options).then(handleJSON);
  },

  uploadLearners({ file, organizationId }) {
    const endpoint = this.endpoints.batch;
    const formData = new FormData();
    formData.append('organization_id', organizationId);
    formData.append('filename', file);

    const customHeaders = {
      authorization: headers().authorization,
    };
    const options = {
      method: 'POST',
      headers: customHeaders,
      body: formData,
    };

    return fetch(endpoint, options).then(handleJSON);
  },

  uploadScheduledLearners({ file, organizationId, activationDateTime }) {
    const endpoint = this.endpoints.batchScheduledLearners;
    const formData = new FormData();
    formData.append('organization_id', organizationId);
    formData.append('scheduled_to_process_at', activationDateTime);
    formData.append('filename', file);

    const customHeaders = {
      authorization: headers().authorization,
    };
    const options = {
      method: 'POST',
      headers: customHeaders,
      body: formData,
    };

    return fetch(endpoint, options).then(handleJSON);
  },

  fetchVpaHistory({ learnerId, format = 'json', callback = handleJSON }) {
    let params = [
      {
        key: 'user_id',
        value: learnerId,
      },
      {
        key: 'format',
        value: format,
      },
    ];

    params = params.map(param => `${param.key}=${param.value}`);
    const paramsQuery = params.join('&');
    const endpoint = `${this.endpoints.vpaHistory}?${paramsQuery}`;

    const requestHeaders = headers();
    if (format === 'csv') {
      requestHeaders.Accept = 'text/csv';
    } else if (format === 'xlsx') {
      requestHeaders.Accept = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    }

    const options = {
      method: 'GET',
      headers: requestHeaders,
    };

    return fetch(endpoint, options).then(callback);
  },
};
